export class BlinkController {
    constructor(blendShapeControls) {
        this.blendShapeControls = blendShapeControls;
        this.isBlinking = false;
        this.nextBlinkTime = performance.now() + 1000;

        // Blink animation state. The open/close motion is stepped from the
        // single render loop (ModelViewer.animate → update) at the capped
        // 60 FPS cadence instead of its own raw requestAnimationFrame loop.
        // That keeps it on the same grid as the other facial controllers and,
        // because update() is gated by !A2FLipSyncDriver.isAnyActive(), a blink
        // that is mid-flight when a spoken reply starts cleanly suspends while
        // A2F owns the eyes and resumes on the capped cadence afterward.
        this._blinkPhase = null;        // null | 'closing' | 'opening'
        this._blinkPhaseStart = 0;
        this._blinkInitialValues = null;
        // performance.now() of the previous update() tick. Used to detect a
        // pause (e.g. while A2F owned the eyes during speech, so update() was
        // skipped) and slide the phase start forward by the gap, so a blink
        // that was mid-flight resumes smoothly afterward instead of snapping.
        this._lastUpdateMs = 0;

        this.config = {
            minBlinkInterval: 3000,
            maxBlinkInterval: 5000,
            blinkDuration: 150,
            enabled: true
        };

        this._blinkSuffixes = [
            'eyeblinkleft', 'eyeblinkright',
            'eyeblink', 'eye_blink',
            'eye_blink_l', 'eye_blink_r',
            'blink',
        ];

        this.blinkShapes = this._detectBlinkShapes();

        if (this.blinkShapes.length === 0) {
            console.warn('[EYE] detect zero blendshapes for eye blink and rotation');
            this.config.enabled = false;
        } else {
            console.log(`[EYE] Blink shapes detected: ${this.blinkShapes.length}`, this.blinkShapes);
        }

        this.setupUI();
    }

    _detectBlinkShapes() {
        const found = [];
        for (const key of Object.keys(this.blendShapeControls)) {
            const low = key.toLowerCase();
            const dotIdx = low.lastIndexOf('.');
            const suffix = dotIdx !== -1 ? low.substring(dotIdx + 1) : low;
            if (this._blinkSuffixes.indexOf(suffix) !== -1) {
                found.push(key);
            }
        }
        return found;
    }

    setupUI() {
        const debugPanel = document.getElementById('debug-panel');
        if (!debugPanel) return;

        const blinkControls = document.createElement('div');
        blinkControls.className = 'mt-3';
        blinkControls.innerHTML = `
            <small class="text-muted">Blink Controls:</small>
            <div class="mb-2">
                <button id="manual-blink" class="btn btn-sm btn-secondary">Trigger Blink</button>
            </div>
            <div class="mb-2">
                <label class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="auto-blink-toggle" checked>
                    <span class="form-check-label">Auto Blink</span>
                </label>
            </div>
            <div class="mb-2">
                <label class="form-label small">Blink Interval (seconds)</label>
                <input type="range" class="form-range" id="blink-interval"
                       min="2" max="8" step="0.5" value="4">
                <small class="text-muted" id="blink-interval-value">4s</small>
            </div>
            <div class="mb-2">
                <label class="form-label small">Blink Speed</label>
                <input type="range" class="form-range" id="blink-speed"
                       min="50" max="300" step="10" value="150">
                <small class="text-muted" id="blink-speed-value">150ms</small>
            </div>
        `;
        debugPanel.appendChild(blinkControls);

        document.getElementById('manual-blink').addEventListener('click', () => this.triggerBlink());
        document.getElementById('auto-blink-toggle').addEventListener('change', (e) => {
            this.config.enabled = e.target.checked;
            if (this.config.enabled) this.scheduleNextBlink();
        });
        document.getElementById('blink-interval').addEventListener('input', (e) => {
            const value = parseFloat(e.target.value);
            this.config.minBlinkInterval = value * 1000 - 1000;
            this.config.maxBlinkInterval = value * 1000 + 1000;
            document.getElementById('blink-interval-value').textContent = `${value}s`;
        });
        document.getElementById('blink-speed').addEventListener('input', (e) => {
            this.config.blinkDuration = parseInt(e.target.value);
            document.getElementById('blink-speed-value').textContent = `${e.target.value}ms`;
        });
    }

    _applyWeight(key, value) {
        const entry = this.blendShapeControls[key];
        const apply = (ctl) => {
            if (ctl && ctl.mesh && typeof ctl.index === 'number') {
                ctl.mesh.morphTargetInfluences[ctl.index] = value;
            }
        };
        if (Array.isArray(entry)) {
            for (const c of entry) apply(c);
        } else {
            apply(entry);
        }
    }

    _getWeight(key) {
        const entry = this.blendShapeControls[key];
        const ctl = Array.isArray(entry) ? entry[0] : entry;
        if (ctl && ctl.mesh && typeof ctl.index === 'number') {
            return ctl.mesh.morphTargetInfluences[ctl.index] || 0;
        }
        return 0;
    }

    triggerBlink() {
        if (this.isBlinking || this.blinkShapes.length === 0) return;

        // Begin the close phase. The open/close motion is stepped from
        // update() on the shared render loop (see _stepBlink), not a private
        // requestAnimationFrame, so a blink in flight when speech starts is
        // simply not stepped (update() is gated off) and never fights A2F.
        this.isBlinking = true;
        this._blinkPhase = 'closing';
        this._blinkPhaseStart = performance.now();
        this._blinkInitialValues = {};
        for (const key of this.blinkShapes) {
            this._blinkInitialValues[key] = this._getWeight(key);
        }
    }

    // Advance an in-flight blink one render frame. Eases the eye-blink shapes
    // toward the current phase's target, then hands off close → open, and on
    // completion clears the blink and schedules the next one.
    _stepBlink(time) {
        // If update() was paused (A2F owned the eyes during speech, or the tab
        // was throttled), slide the phase start forward by the gap so the blink
        // resumes from where it left off instead of jumping straight to the end.
        const gap = this._lastUpdateMs ? time - this._lastUpdateMs : 0;
        if (gap > 200) {
            this._blinkPhaseStart += gap;
        }

        const half = Math.max(1, this.config.blinkDuration / 2);
        const target = this._blinkPhase === 'closing' ? 1.0 : 0.0;
        let progress = (time - this._blinkPhaseStart) / half;
        if (progress < 0) progress = 0;
        else if (progress > 1) progress = 1;
        const eased = progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        for (const key of this.blinkShapes) {
            const initial = this._blinkInitialValues[key] || 0;
            this._applyWeight(key, initial + (target - initial) * eased);
        }

        if (progress >= 1) {
            for (const key of this.blinkShapes) {
                this._applyWeight(key, target);
            }
            if (this._blinkPhase === 'closing') {
                // Eyes fully shut — flip into the open phase.
                this._blinkPhase = 'opening';
                this._blinkPhaseStart = time;
                this._blinkInitialValues = {};
                for (const key of this.blinkShapes) {
                    this._blinkInitialValues[key] = target;
                }
            } else {
                // Eyes fully open again — blink complete.
                this._blinkPhase = null;
                this._blinkInitialValues = null;
                this.isBlinking = false;
                this.scheduleNextBlink();
            }
        }
    }

    update(time) {
        // Always finish an in-flight blink — even if auto-blink was just
        // toggled off — so the eyes are never stranded mid-blink (e.g. shut).
        if (this._blinkPhase) {
            this._stepBlink(time);
            this._lastUpdateMs = time;
            return;
        }

        this._lastUpdateMs = time;
        if (!this.config.enabled) return;

        if (time >= this.nextBlinkTime) {
            this.triggerBlink();
        }
    }

    scheduleNextBlink() {
        const now = performance.now();
        const interval = Math.random() *
            (this.config.maxBlinkInterval - this.config.minBlinkInterval) +
            this.config.minBlinkInterval;
        this.nextBlinkTime = now + interval;
    }
}
